import axios from "axios";
import dayjs from "dayjs";

const API_URL = "https://apidash.smartpress.kz/api";
const STORAGE_URL = "https://apidash.smartpress.kz/storage";

const getToken = () => localStorage.getItem("token");

const getActiveUser = () => {
  try {
    return JSON.parse(localStorage.getItem("activeUser")) || {};
  } catch (error) {
    console.error("Failed to parse activeUser:", error);
    return {};
  }
};

const getAuthConfig = () => ({
  headers: {
    Authorization: `Bearer ${getToken()}`,
  },
});

const normalizeAvatar = (avatar, fallback = null) => {
  if (!avatar) {
    return fallback;
  }

  if (
    avatar.startsWith("http://") ||
    avatar.startsWith("https://") ||
    avatar.startsWith("data:")
  ) {
    return avatar;
  }

  return `${STORAGE_URL}/${avatar}`;
};

const state = () => ({
  settingToggle: false,
  openinfo: true,

  activechat: false,
  activeChatId: null,

  searchContact: "",
  mobileChatSidebar: false,

  profileinfo: {},
  user: {},
  contacts: [],
  chats: [],
  messFeed: [],

  contactsLoading: false,
  messagesLoading: false,

  contactsError: null,
  messagesError: null,
});

const getters = {
  getprofileinfo: (state) => state.profileinfo,

  getContacts: (state) => state.contacts,

  getChats: (state) => state.chats,

  getMessFeed: (state) => state.messFeed,

  getUser: (state) => state.user,

  getConversationId: (state) => state.activeChatId,

  getChatType: (state) => state.user?.chatType || null,

  getChatName: (state) => {
    return state.user?.chatType === "group"
      ? state.user.chatName
      : null;
  },

  getChatAvatar: (state) => {
    return state.user?.chatType === "group"
      ? state.user.chatAvatar
      : null;
  },

  isChatActive: (state) => state.activechat,

  isMobileChatSidebarOpen: (state) =>
    state.mobileChatSidebar,

  isContactsLoading: (state) =>
    state.contactsLoading,

  isMessagesLoading: (state) =>
    state.messagesLoading,

  getContactsError: (state) =>
    state.contactsError,

  getMessagesError: (state) =>
    state.messagesError,
};

const mutations = {
  REMOVE_CONVERSATION(state, conversationId) {
  state.contacts = state.contacts.filter(
    (contact) =>
      Number(contact.id) !== Number(conversationId)
  );
},
  SET_SETTING_TOGGLE(state, value) {
    state.settingToggle = value;
  },

  SET_OPEN_INFO(state, value) {
    state.openinfo = value;
  },

  SET_ACTIVE_CHAT(state, value) {
    state.activechat = value;
  },

  SET_ACTIVE_CHAT_ID(state, conversationId) {
    state.activeChatId = conversationId;
  },

  SET_MOBILE_CHAT_SIDEBAR(state, value) {
    state.mobileChatSidebar = value;
  },

  SET_CONTACTS(state, contacts) {
    state.contacts = Array.isArray(contacts)
      ? contacts
      : [];
  },

  SET_USER(state, user) {
    state.user = user || {};
  },

  SET_MESSAGE_FEED(state, messages) {
    state.messFeed = Array.isArray(messages)
      ? messages
      : [];
  },

  ADD_MESSAGE(state, message) {
    state.messFeed.push(message);
  },

  SET_CONTACTS_LOADING(state, value) {
    state.contactsLoading = value;
  },

  SET_MESSAGES_LOADING(state, value) {
    state.messagesLoading = value;
  },

  SET_CONTACTS_ERROR(state, error) {
    state.contactsError = error;
  },

  SET_MESSAGES_ERROR(state, error) {
    state.messagesError = error;
  },

  RESET_ACTIVE_CHAT(state) {
    state.activechat = false;
    state.activeChatId = null;
    state.user = {};
    state.messFeed = [];
    state.openinfo = true;
  },

  // Старые имена оставлены для совместимости
  toggleuserSetting(state) {
    state.settingToggle = !state.settingToggle;
  },

  sendMessage(state, payload) {
    state.messFeed.push(payload);
  },

  openinfo(state) {
    state.openinfo = !state.openinfo;
  },

  setOpenInfo(state, value) {
    state.openinfo = value;
  },

  setContacts(state, contacts) {
    state.contacts = Array.isArray(contacts)
      ? contacts
      : [];
  },

  setUser(state, user) {
    state.user = user || {};
  },

  addMessage(state, message) {
    state.messFeed.push(message);
  },
};

const actions = {
  async archiveConversation(
  { commit, state },
  conversationId
) {
  try {
    const response = await axios.patch(
      `${API_URL}/conversations/${conversationId}/archive`,
      {},
      getAuthConfig()
    );

    const isCurrentConversation =
      Number(state.activeChatId) ===
      Number(conversationId);

    commit(
      "REMOVE_CONVERSATION",
      conversationId
    );

    if (isCurrentConversation) {
      commit("RESET_ACTIVE_CHAT");
    }

    return response.data;
  } catch (error) {
    console.error(
      "Failed to archive conversation:",
      error
    );

    throw error;
  }
},
  toggleuserSetting({ commit, state }) {
    commit(
      "SET_SETTING_TOGGLE",
      !state.settingToggle
    );
  },

  sendMessage({ commit }, payload) {
    commit("ADD_MESSAGE", payload);
  },

  addMessage({ commit }, message) {
    commit("ADD_MESSAGE", message);
  },

  openinfo({ commit, state }) {
    commit("SET_OPEN_INFO", !state.openinfo);
  },

  setOpenInfo({ commit }, value) {
    commit("SET_OPEN_INFO", Boolean(value));
  },

  openMobileSidebar({ commit }) {
    // Именно открыть, а не переключить
    commit("SET_MOBILE_CHAT_SIDEBAR", true);
  },

  closeMobileSidebar({ commit }) {
    commit("SET_MOBILE_CHAT_SIDEBAR", false);
  },

  toggleMobileSidebar({ commit, state }) {
    commit(
      "SET_MOBILE_CHAT_SIDEBAR",
      !state.mobileChatSidebar
    );
  },

  closeChat({ commit }) {
    commit("RESET_ACTIVE_CHAT");
    commit("SET_MOBILE_CHAT_SIDEBAR", false);
  },

  async openChat(
    { commit, dispatch, state },
    conversationId
  ) {
    if (!conversationId) {
      throw new Error("Conversation ID is required.");
    }

    const token = getToken();
    const activeUser = getActiveUser();
    const activeUserId = Number(activeUser.id);

    if (!token) {
      throw new Error("Authorization token is missing.");
    }

    commit("SET_MESSAGES_LOADING", true);
    commit("SET_MESSAGES_ERROR", null);

    try {
      /*
       * Сначала находим разговор в уже загруженном списке.
       * Если список пустой или разговор отсутствует,
       * повторно загружаем conversations.
       */
      let selectedContact = state.contacts.find(
        (contact) =>
          Number(contact.id) === Number(conversationId)
      );

      if (!selectedContact) {
        await dispatch("fetchContacts");

        selectedContact = state.contacts.find(
          (contact) =>
            Number(contact.id) === Number(conversationId)
        );
      }

      if (!selectedContact) {
        throw new Error(
          `Conversation ${conversationId} was not found.`
        );
      }

      const response = await axios.get(
        `${API_URL}/messages/${conversationId}`,
        getAuthConfig()
      );

      const sourceMessages = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      const messages = sourceMessages.map((message) => {
        const isOwnMessage =
          Number(message.user_id) === activeUserId;

        const messageUser = message.user || {};

        return {
          ...message,

          sender: isOwnMessage ? "me" : "them",

          avatar: normalizeAvatar(
            isOwnMessage
              ? activeUser.avatar
              : messageUser.avatar
          ),

          time: message.created_at
            ? dayjs(message.created_at).format("HH:mm")
            : "",

          userName: isOwnMessage
            ? activeUser.name || "You"
            : messageUser.name || "Unknown",
        };
      });

      const isGroup =
        selectedContact.chatType === "group";

      const chatUser = {
        id: selectedContact.id,
        chatType:
          selectedContact.chatType || "private",

        chatName: isGroup
          ? selectedContact.chatName ||
            selectedContact.fullName ||
            "Group chat"
          : null,

        chatAvatar: isGroup
          ? selectedContact.chatAvatar ||
            selectedContact.avatar
          : null,

        fullName: !isGroup
          ? selectedContact.fullName ||
            "Conversation"
          : null,

        status: !isGroup
          ? selectedContact.status || "offline"
          : null,

        avatar: selectedContact.avatar || null,

        created_by:
          selectedContact.created_by || null,
      };

      /*
       * Сначала показываем чат.
       * Пользователь не должен ждать, пока выполнится
       * запрос отметки сообщений прочитанными.
       */
      commit("SET_USER", chatUser);
      commit("SET_MESSAGE_FEED", messages);
      commit(
        "SET_ACTIVE_CHAT_ID",
        Number(conversationId)
      );
      commit("SET_ACTIVE_CHAT", true);
      commit("SET_OPEN_INFO", true);
      commit("SET_MOBILE_CHAT_SIDEBAR", false);

      /*
       * Отметка прочитанным — отдельный запрос.
       * Его ошибка не должна закрывать уже открытый чат.
       */
      try {
        await axios.post(
          `${API_URL}/conversations/${conversationId}/read`,
          {},
          getAuthConfig()
        );
      } catch (readError) {
        console.error(
          "Failed to mark conversation as read:",
          readError
        );
      }

      /*
       * Обновляем счётчики непрочитанных.
       * Не используем await, чтобы не задерживать открытие чата.
       */
      dispatch("fetchContacts").catch((error) => {
        console.error(
          "Failed to refresh conversations:",
          error
        );
      });

      return {
        conversation: chatUser,
        messages,
      };
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to open conversation.";

      commit("SET_MESSAGES_ERROR", message);

      console.error(
        "Error fetching messages for conversation:",
        error
      );

      // Важно: пробрасываем ошибку в index.vue
      throw error;
    } finally {
      commit("SET_MESSAGES_LOADING", false);
    }
  },

  async fetchContacts({ commit }) {
    const token = getToken();

    if (!token) {
      commit(
        "SET_CONTACTS_ERROR",
        "Authorization token is missing."
      );
      commit("SET_CONTACTS", []);

      throw new Error(
        "Authorization token is missing."
      );
    }

    commit("SET_CONTACTS_LOADING", true);
    commit("SET_CONTACTS_ERROR", null);

    try {
      const response = await axios.get(
        `${API_URL}/conversations`,
        getAuthConfig()
      );

      const sourceConversations = Array.isArray(
        response.data
      )
        ? response.data
        : response.data?.data || [];

      const formattedContacts =
        sourceConversations.map((conversation) => {
          const chatType =
            conversation.type ||
            conversation.chatType ||
            "private";

          const isGroup = chatType === "group";

          const fullName =
            conversation.fullName ||
            conversation.full_name ||
            conversation.user?.name ||
            null;

          const chatName =
            conversation.chat_name ||
            conversation.chatName ||
            null;

          const rawAvatar =
            conversation.avatar ||
            conversation.user?.avatar ||
            null;

          const rawChatAvatar =
            conversation.chat_avatar ||
            conversation.chatAvatar ||
            null;

          return {
            id: conversation.id,

            fullName: isGroup
              ? chatName || fullName || "Group chat"
              : fullName || "Conversation",

            lastmessage:
              conversation.lastMessage ||
              conversation.last_message ||
              "",

            lastmessageTime:
              conversation.lastMessageTime ||
              conversation.last_message_time
                ? dayjs(
                    conversation.lastMessageTime ||
                      conversation.last_message_time
                  ).format("HH:mm")
                : "",

            unredmessage: Number(
              conversation.unreadMessages ??
                conversation.unread_messages ??
                conversation.unread_count ??
                0
            ),

            avatar: normalizeAvatar(rawAvatar),

            status:
              conversation.statusUser === "active" ||
              conversation.statusUser === "online" ||
              conversation.status_user === "active" ||
              conversation.status_user === "online"
                ? "active"
                : "offline",

            created_by:
              conversation.created_by || null,

            chatName,

            chatType,

            chatAvatar: normalizeAvatar(
              rawChatAvatar,
              normalizeAvatar(rawAvatar)
            ),

            application_id:
              conversation.application_id || null,

            offer_id:
              conversation.offer_id || null,

            tech_card_id:
              conversation.tech_card_id || null,

            project_id:
              conversation.project_id || null,
          };
        });

      /*
       * Сначала непрочитанные, затем более свежие.
       * Если сервер уже возвращает правильный порядок,
       * этот sort можно убрать.
       */
      formattedContacts.sort((a, b) => {
        const unreadDifference =
          Number(b.unredmessage || 0) -
          Number(a.unredmessage || 0);

        if (unreadDifference !== 0) {
          return unreadDifference;
        }

        return String(
          b.lastmessageTime || ""
        ).localeCompare(
          String(a.lastmessageTime || "")
        );
      });

      commit("SET_CONTACTS", formattedContacts);

      return formattedContacts;
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to load conversations.";

      commit("SET_CONTACTS_ERROR", message);
      commit("SET_CONTACTS", []);

      console.error(
        "Error fetching conversations:",
        error
      );

      throw error;
    } finally {
      commit("SET_CONTACTS_LOADING", false);
    }
  },

  async fetchUser({ commit }, userId) {
    if (!userId) {
      throw new Error("User ID is required.");
    }

    try {
      const response = await axios.get(
        `${API_URL}/user/${userId}`,
        getAuthConfig()
      );

      commit("SET_USER", response.data || {});

      return response.data;
    } catch (error) {
      console.error("Error fetching user:", error);
      throw error;
    }
  },
};

export default {
  namespaced: false,
  state,
  getters,
  mutations,
  actions,
};